import { cloneDeep } from 'lodash';
import { type IFramework } from '@/framework';
import type { TextRenderSnapshot } from '@/framework/services/text-render';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { type TextSelectionRange } from '@/utils/text-kit';
import {
    transformArtTextDataToStage,
    transformGlowToStage,
    transformLetterSpacingToStage,
    transformShadowToStage,
    transformStrokeToStage,
} from '@/utils/transform-kit';
import {
    LayerType,
    MaterialIdsType,
    TextWritingType,
    type IAdvancedTextMaterial,
    type ILayerProps,
    type ITextContent,
    type ITextLayerProps,
    type Optional,
} from '@/version-types';
import { type FormatBrushData } from '../types';
import {
    applySnapshotSize,
    collectFontMaterialIDs,
    collectFontSubsetRequests,
    createTextContentsByArtText,
    pushHistory,
    requestTextSVGPreview,
    resolveArtTextCharacterTarget,
    updateLayer,
} from '../utils';
import { getTextLayerHost } from '../utils/host';

export type ArtTextCharacterConfig = Partial<
    Pick<
        ITextContent,
        'color' | 'bold' | 'italic' | 'lineThrough' | 'underline' | 'rotate' | 'scale' | 'offsetX' | 'offsetY'
    >
>;

export interface IArtText {
    /**
     * 应用花字效果。
     *
     * @param layerID - 图层 ID。
     * @param data - 花字特效数据。
     * @param options - 历史和格式刷选项。
     * @returns 准备和提交成功后的图层；校验或准备失败时无返回值。
     */
    applyArtText(
        layerID: string,
        data: IAdvancedTextMaterial,
        options?: {
            activate?: boolean;
            writeHistory?: boolean;
            formatBrushData?: FormatBrushData;
            historyFuncId?: string;
        },
    ): Promise<ILayerProps | void>;

    /**
     * 取消花字效果。
     *
     * @param layerID - 图层 ID。
     * @param options - 历史选项。
     * @returns 更新后的图层；图层无花字时无返回值。
     */
    cancelArtText(layerID: string, options?: { writeHistory?: boolean }): ILayerProps | void;

    /**
     * 调整选区命中的花字重复位。
     *
     * @param layerID - 图层 ID。
     * @param partialConfig - 要更新的字符属性。
     * @param options - UTF-8 选区和历史选项。
     * @returns 更新后的图层；选区未命中单字符时无返回值。
     */
    adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options: {
            range: TextSelectionRange | null;
            writeHistory?: boolean;
        },
    ): ILayerProps | void;
}

export class ArtText implements IArtText {
    private prevLayer: ILayerProps | null = null;
    private readonly applyTokens = new Map<string, symbol>();
    private applySequence = 0;

    /**
     * 创建花字领域对象。
     *
     * @param framework - 当前编辑器框架实例。
     * @returns ArtText 领域对象。
     */
    constructor(private framework: IFramework) {}

    /**
     * 应用花字基础排版属性。
     *
     * @param layerData - 原始文本图层数据。
     * @param data - 花字特效数据。
     * @returns 应用字体和字距后的候选数据。
     */
    private attachBaseProps(layerData: ITextLayerProps, data: IAdvancedTextMaterial): ITextLayerProps {
        const standardTextContent = data.textContents[0];
        return {
            ...layerData,
            fontFamily: standardTextContent.fontFamily,
            letterSpacing: transformLetterSpacingToStage(data.material.defaultLetterSpacing, layerData.fontSize),
        };
    }

    /**
     * 应用花字图层级特效属性。
     *
     * @param layerData - 原始文本图层数据。
     * @param data - 花字特效数据。
     * @param ratio - 当前字号与素材默认字号的比例。
     * @returns 应用描边、阴影和发光后的候选数据。
     */
    private attachEffectProps(layerData: ITextLayerProps, data: IAdvancedTextMaterial, ratio: number): ITextLayerProps {
        const nextLayerData: ITextLayerProps = { ...layerData, textStroke: [], textShadow: [], textGlow: [] };
        const standardTextContent = data.textContents[0];

        if (standardTextContent.textStroke?.length) {
            nextLayerData.textStroke = transformStrokeToStage(
                standardTextContent.textStroke,
                data.material.defaultFontSize,
            ).map((item) => ({ ...item, width: Math.round(item.width * ratio) }));
        }
        if (standardTextContent.textShadow?.length) {
            nextLayerData.textShadow = transformShadowToStage(
                standardTextContent.textShadow,
                data.material.defaultFontSize,
            ).map((item) => ({ ...item, x: Math.round(item.x * ratio), y: Math.round(item.y * ratio) }));
        }
        if (standardTextContent.textGlow?.length) {
            nextLayerData.textGlow = transformGlowToStage(
                standardTextContent.textGlow,
                data.material.defaultFontSize,
            ).map((item) => ({ ...item, width: Math.round(item.width * ratio) }));
        }

        return nextLayerData;
    }

    /**
     * 移除花字字符专属变换，保留普通富文本样式。
     *
     * @param layerData - 当前文本图层数据。
     * @returns 清理 rotate、scale 和 offset 后的数据。
     */
    private resetTextContents(layerData: ITextLayerProps): ITextLayerProps {
        if (!layerData.textContents?.length) return layerData;

        const textContents = layerData.textContents.map((item) => {
            const next = { ...item };
            const dropped: (keyof ITextContent)[] = ['rotate', 'offsetX', 'offsetY', 'scale'];
            dropped.forEach((key) => Reflect.deleteProperty(next, key));
            return next;
        });

        return { ...layerData, textContents };
    }

    /**
     * 在提交图层前准备候选数据需要的全部字体子集。
     *
     * @param layer - 尚未写入舞台的候选图层。
     * @returns 所有字体和字符子集均准备成功时返回 true。
     */
    private async prepareFontSubsets(layer: ILayerProps): Promise<boolean> {
        const requests = collectFontSubsetRequests(layer);
        if (requests.length === 0) return true;

        const results = await Promise.all(
            requests.map(async ({ fontFamily, chars }) => {
                if (!this.framework.service.font.getByFamily(fontFamily)) return false;
                const subsetFamily = await this.framework.service.font.loadSubset(fontFamily, chars);
                if (!subsetFamily) return false;

                setLayerRuntimeValue(layer, {
                    fontFamilyMap: {
                        ...(layer.__runtime?.fontFamilyMap || {}),
                        [fontFamily]: subsetFamily,
                    },
                });
                return true;
            }),
        );
        return results.every(Boolean);
    }

    /**
     * 将候选图层提交到舞台、连续预览和历史。
     *
     * @param prevLayer - 本次连续交互的基线图层。
     * @param nextLayer - 已完成准备的候选图层。
     * @param writeHistory - 是否结束连续交互并写入历史。
     * @param snapshot - 候选图层对应的排版快照。
     * @returns 已提交的候选图层。
     */
    private commit(
        prevLayer: ILayerProps,
        nextLayer: ILayerProps,
        writeHistory: boolean,
        snapshot?: Optional<TextRenderSnapshot>,
    ): ILayerProps {
        if (writeHistory) requestTextSVGPreview(this.framework, 'commit', nextLayer);
        else if (snapshot) requestTextSVGPreview(this.framework, 'update', nextLayer);
        updateLayer(this.framework, nextLayer, {
            activate: true,
            dataOnly: Boolean(snapshot) && !writeHistory,
        });
        if (writeHistory) {
            pushHistory(this.framework, prevLayer, nextLayer);
            this.prevLayer = null;
        }
        return nextLayer;
    }

    /**
     * 应用花字效果。
     *
     * @param layerID - 图层 ID。
     * @param data - 花字特效数据。
     * @param options - 历史和格式刷选项。
     * @returns 准备和提交成功后的图层；校验或准备失败时无返回值。
     */
    async applyArtText(
        layerID: string,
        data: IAdvancedTextMaterial,
        options: {
            activate?: boolean;
            writeHistory?: boolean;
            formatBrushData?: FormatBrushData;
            historyFuncId?: string;
        } = {},
    ) {
        const sourceLayer = this.framework.service.flatLayer.getLayer(layerID);
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (
            !prevLayer ||
            prevLayer.layerType !== LayerType.TEXT_LAYER ||
            !data.textContents[0] ||
            data.material.defaultFontSize <= 0
        ) {
            return;
        }

        const { writeHistory = true, formatBrushData } = options;
        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (
            !formatBrushData &&
            (prevLayerData.writingMode !== TextWritingType.HORIZONTAL_TB || prevLayerData.shapeExtension)
        ) {
            return;
        }
        if (prevLayerData.advancedTextMaterial?.id === data.id) return;

        const ratio = prevLayerData.fontSize / data.material.defaultFontSize;
        let nextLayerData = this.resetTextContents(cloneDeep(prevLayerData));
        nextLayerData = this.attachBaseProps(nextLayerData, data);
        nextLayerData = this.attachEffectProps(nextLayerData, data, ratio);
        nextLayerData = { ...nextLayerData, advancedTextMaterial: transformArtTextDataToStage(data) };
        nextLayerData = {
            ...nextLayerData,
            textContents: createTextContentsByArtText(prevLayerData.text, nextLayerData),
        };

        const nextExtraInfo =
            prevLayer.extraInfo && Object.prototype.toString.call(prevLayer.extraInfo) === '[object Object]'
                ? { ...prevLayer.extraInfo }
                : {};
        let nextLayer: ILayerProps = {
            ...prevLayer,
            data: nextLayerData,
            extraInfo: nextExtraInfo,
            __runtime: cloneDeep(prevLayer.__runtime),
        };
        let removedShapeText = false;

        if (formatBrushData) {
            const overrideColor = formatBrushData.__overrideColorInArtTextCase;
            nextLayerData = {
                ...nextLayerData,
                ...formatBrushData.data,
                textContents: nextLayerData.textContents!.map((item) => ({
                    ...item,
                    fontSize: formatBrushData.data.fontSize,
                    color: overrideColor || item.color,
                })),
            };
            nextLayer = { ...nextLayer, data: nextLayerData, opacity: formatBrushData.opacity };
            if (overrideColor) nextExtraInfo.overrideColorInArtTextCase = overrideColor;
            if (nextLayerData.shapeExtension) {
                removedShapeText = true;
                Reflect.deleteProperty(nextLayerData, 'shapeExtension');
                Reflect.deleteProperty(nextLayerData, 'screenshotUrl');
            }
            if (nextLayerData.writingMode !== TextWritingType.HORIZONTAL_TB) {
                nextLayerData.writingMode = TextWritingType.HORIZONTAL_TB;
            }
            nextLayer = { ...nextLayer, data: nextLayerData };
        }

        const materialIds = nextLayer.materialIds ? cloneDeep(nextLayer.materialIds) : {};
        if (removedShapeText) Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_TRANSFORM);

        materialIds[MaterialIdsType.FONT_FAMILY] = collectFontMaterialIDs(this.framework.service.font, nextLayer);
        materialIds[MaterialIdsType.ADVANCED_TEXT_MATERIAL] = [data.id];

        if (nextLayerData.textMark) {
            materialIds[MaterialIdsType.TEXT_MARK] = [nextLayerData.textMark.materialId];
        } else {
            Reflect.deleteProperty(materialIds, MaterialIdsType.TEXT_MARK);
        }

        if (formatBrushData?.materialIds?.[MaterialIdsType.TEXT_EFFECT]) {
            materialIds[MaterialIdsType.TEXT_EFFECT] = formatBrushData.materialIds[MaterialIdsType.TEXT_EFFECT];
        }
        nextLayer = { ...nextLayer, materialIds, extraInfo: nextExtraInfo };

        const applyToken = Symbol(layerID);
        const loadingKey = `text-layer:art-text-apply:${++this.applySequence}`;

        this.applyTokens.set(layerID, applyToken);
        this.framework.service.loading.add(prevLayer.id, loadingKey);

        try {
            if (!(await this.prepareFontSubsets(nextLayer))) return;
            if (
                this.applyTokens.get(layerID) !== applyToken ||
                this.framework.service.flatLayer.getLayer(layerID) !== sourceLayer
            ) {
                return;
            }

            const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
            // 花字属性与候选尺寸只在当前排版 ready 时共同提交。
            if (resolution?.status !== 'ready') return;
            const snapshot = resolution.value;

            nextLayer = applySnapshotSize(nextLayer, snapshot);
            return this.commit(prevLayer, nextLayer, writeHistory, snapshot);
        } finally {
            this.framework.service.loading.remove(prevLayer.id, loadingKey);
            if (this.applyTokens.get(layerID) === applyToken) this.applyTokens.delete(layerID);
        }
    }

    /**
     * 取消花字效果。
     *
     * @param layerID - 图层 ID。
     * @param options - 历史选项。
     * @returns 更新后的图层；图层无花字时无返回值。
     */
    cancelArtText(layerID: string, options: { writeHistory?: boolean } = {}) {
        const prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (!prevLayerData.advancedTextMaterial) return;

        const nextLayerData: ITextLayerProps = {
            ...this.resetTextContents(cloneDeep(prevLayerData)),
            textStroke: [],
            textShadow: [],
            textGlow: [],
        };
        Reflect.deleteProperty(nextLayerData, 'advancedTextMaterial');

        const materialIds = prevLayer.materialIds ? cloneDeep(prevLayer.materialIds) : {};
        Reflect.deleteProperty(materialIds, MaterialIdsType.ADVANCED_TEXT_MATERIAL);
        const extraInfo = prevLayer.extraInfo ? cloneDeep(prevLayer.extraInfo) : {};
        Reflect.deleteProperty(extraInfo, 'overrideColorInArtTextCase');

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData, materialIds, extraInfo };
        const { writeHistory = true } = options;
        if (writeHistory) return this.commit(prevLayer, nextLayer, true);

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 连续预览必须有当前 ready 几何，不能以旧尺寸提交新的花字数据。
        if (resolution?.status !== 'ready') return;
        return this.commit(prevLayer, nextLayer, false, resolution.value);
    }

    /**
     * 调整选区命中的花字重复位。
     *
     * @param layerID - 图层 ID。
     * @param partialConfig - 要更新的字符属性。
     * @param options - UTF-8 选区和历史选项。
     * @returns 更新后的图层；选区未命中单字符时无返回值。
     */
    adjustArtTextSingleCharacter(
        layerID: string,
        partialConfig: ArtTextCharacterConfig,
        options: { range: TextSelectionRange | null; writeHistory?: boolean },
    ) {
        const prevLayer = (() => {
            if (this.prevLayer?.id === layerID) return this.prevLayer;
            this.prevLayer = this.framework.service.flatLayer.getLayer(layerID, true);
            return this.prevLayer;
        })();
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const { range, writeHistory = false } = options;
        const prevLayerData = prevLayer.data as ITextLayerProps;
        if (prevLayerData.writingMode !== TextWritingType.HORIZONTAL_TB || prevLayerData.shapeExtension) return;
        const prevTextContents = prevLayerData.textContents ?? [];
        const prevAdvancedTextMaterial = prevLayerData.advancedTextMaterial;
        if (prevTextContents.length === 0 || !prevAdvancedTextMaterial) return;

        const target = resolveArtTextCharacterTarget(prevLayerData.text, range);
        if (target === null) return;

        const nextAdvancedTextMaterial = cloneDeep(prevAdvancedTextMaterial);
        let nextTextContents: ITextContent[];
        if (target === 'all') {
            nextAdvancedTextMaterial.textContents = nextAdvancedTextMaterial.textContents.map((item) => ({
                ...item,
                ...partialConfig,
            }));
            nextTextContents = prevTextContents.map((item) => ({ ...item, ...partialConfig }));
        } else {
            const useCount = prevAdvancedTextMaterial.material.useCount || 1;
            const slot = target % useCount;
            const preset = nextAdvancedTextMaterial.textContents[slot] ?? nextAdvancedTextMaterial.textContents[0];
            if (!preset) return;

            nextAdvancedTextMaterial.textContents[slot] = { ...preset, ...partialConfig };
            nextTextContents = prevTextContents.map((item, index) =>
                index % useCount === slot ? { ...item, ...partialConfig } : item,
            );
        }

        const nextLayer: ILayerProps = {
            ...prevLayer,
            data: {
                ...prevLayerData,
                advancedTextMaterial: nextAdvancedTextMaterial,
                textContents: nextTextContents,
            },
        };
        if (writeHistory) return this.commit(prevLayer, nextLayer, true);

        const resolution = getTextLayerHost(this.framework.service.edgelessCanvas, nextLayer.id)?.layout(nextLayer);
        // 连续单字调整只发布当前 ready 的几何与视觉。
        if (resolution?.status !== 'ready') return;
        return this.commit(prevLayer, nextLayer, false, resolution.value);
    }
}
